import React from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const UnitsTable = ({ 
  units, 
  selectedUnits, 
  onSelectUnit, 
  onSelectAll, 
  onViewDetails,
  onEditUnit 
}) => {
  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-success/10 text-success border-success/20',
      maintenance: 'bg-warning/10 text-warning border-warning/20',
      retired: 'bg-muted text-muted-foreground border-border',
      available: 'bg-accent/10 text-accent border-accent/20'
    };
    return colors?.[status] || colors?.available;
  };

  const getStatusIcon = (status) => {
    const icons = {
      active: 'CheckCircle',
      maintenance: 'Wrench',
      retired: 'XCircle',
      available: 'Circle'
    };
    return icons?.[status] || 'Circle';
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('en-US')?.format(mileage);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <Checkbox
                  checked={selectedUnits?.length === units?.length && units?.length > 0}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                  indeterminate={selectedUnits?.length > 0 && selectedUnits?.length < units?.length}
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Unit Number
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                VIN
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Make/Model
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Year
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Assignment
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Yard
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Mileage
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Engine
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                ESN
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {units?.map((unit) => (
              <tr 
                key={unit?.id}
                className="hover:bg-muted/30 transition-hover"
              >
                <td className="px-4 py-4">
                  <Checkbox
                    checked={selectedUnits?.includes(unit?.id)}
                    onChange={() => onSelectUnit(unit?.id)}
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="Truck" size={18} strokeWidth={2} className="text-primary" />
                    <span className="text-sm font-medium text-foreground">{unit?.unitNumber}</span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-muted-foreground font-mono">{unit?.vin}</span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">{unit?.make}</span>
                    <span className="text-xs text-muted-foreground">{unit?.model}</span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-foreground">{unit?.year}</span>
                </td>
                <td className="px-4 py-4">
                  {unit?.assignment ? (
                    <div className="flex items-center space-x-2">
                      <Icon name="User" size={16} strokeWidth={2} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">{unit?.assignment}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground italic">Unassigned</span>
                  )}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="MapPin" size={16} strokeWidth={2} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{unit?.yard}</span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-foreground">{formatMileage(unit?.mileage)} mi</span>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-foreground">{unit?.engine || <span className="text-muted-foreground italic">—</span>}</span>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-foreground font-mono">{unit?.esn || <span className="text-muted-foreground italic">—</span>}</span>
                </td>
                <td className="px-4 py-4">
                  <div className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${getStatusColor(unit?.status)}`}>
                    <Icon name={getStatusIcon(unit?.status)} size={14} strokeWidth={2} />
                    <span className="capitalize">{unit?.status}</span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onViewDetails(unit)}
                      className="flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md transition-hover"
                      title="View Details"
                    >
                      <Icon name="Eye" size={18} strokeWidth={2} />
                    </button>
                    <button
                      onClick={() => onEditUnit(unit)}
                      className="flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md transition-hover"
                      title="Edit Unit"
                    >
                      <Icon name="Edit" size={18} strokeWidth={2} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UnitsTable;